import React, { useCallback, useState } from "react";
import { BADGE_MODIFIER } from "../constants";
import type { Template, TemplateCategory } from "../constants";
import styles from "../../../../styles/mailCenter/emailTemplatesTab.module.scss";
import CreateTemplateModal from "../components/CreateTemplateModal";
import TemplatePreviewModal from "../components/TemplatePreviewModal";
import DeleteTemplateModal from "../components/DeleteTemplateModal";
import EditTemplateModal from "../components/EditTemplateModal";
import axios from "axios";
import { isAuth } from "../../../../pages/auth/ProtectedRoute";
import { useQuery } from "@tanstack/react-query";
import TemplateCardSkeleton from "../components/TemplateCardSkeleton";

 interface NewTemplatePayload {
  description?: string;
  name: string;
  subject: string;
  body: string;
}

interface CategoryBadgeProps {
  category: TemplateCategory;
}

interface TemplatesToolbarProps {
  search: string;
  onSearch: (value: string) => void;
  onNewTemplate: () => void;
}

interface TemplateCardProps {
  template: Template;
  onPreview: (template: Template) => void;
  onDelete: (id: number | string) => void;
  onEdit: (template: Template) => void;
}

interface ActionButton {
  icon: React.ReactNode;
  label: string;
  danger: boolean;
}

const formatDate = (timestamp: string | number | Date): string => {
  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const CategoryBadge: React.FC<CategoryBadgeProps> = ({ category }) => (
  <span
    className={[
      styles["template-card__badge"],
      styles[`template-card__${BADGE_MODIFIER[category]}`],
    ].join(" ")}
  >
    {category}
  </span>
);

const ACTION_BUTTONS: ActionButton[] = [
  {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.4506 3.87232C13.8031 3.51993 14.0011 3.04196 14.0012 2.54355C14.0012 2.04514 13.8033 1.56712 13.4509 1.21465C13.0985 0.862177 12.6206 0.664125 12.1222 0.664063C11.6238 0.664 11.1457 0.861932 10.7933 1.21432L1.89593 10.1137C1.74114 10.268 1.62667 10.458 1.56259 10.667L0.681925 13.5683C0.664695 13.626 0.663394 13.6872 0.678159 13.7456C0.692924 13.8039 0.723205 13.8571 0.765789 13.8997C0.808373 13.9422 0.861672 13.9724 0.920031 13.987C0.978389 14.0017 1.03963 14.0003 1.09726 13.983L3.99926 13.103C4.20804 13.0395 4.39804 12.9257 4.55259 12.7717L13.4506 3.87232Z"
          stroke="#565656"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    label: "Edit",
    danger: false,
  },
  // {
  //   icon: (
  //     <svg
  //       width="16"
  //       height="16"
  //       viewBox="0 0 16 16"
  //       fill="none"
  //       xmlns="http://www.w3.org/2000/svg"
  //     >
  //       <path
  //         d="M13.332 5.33594H6.66536C5.92898 5.33594 5.33203 5.93289 5.33203 6.66927V13.3359C5.33203 14.0723 5.92898 14.6693 6.66536 14.6693H13.332C14.0684 14.6693 14.6654 14.0723 14.6654 13.3359V6.66927C14.6654 5.93289 14.0684 5.33594 13.332 5.33594Z"
  //         stroke="#565656"
  //         strokeWidth="1.33333"
  //         strokeLinecap="round"
  //         strokeLinejoin="round"
  //       />
  //       <path
  //         d="M2.66536 10.6693C1.93203 10.6693 1.33203 10.0693 1.33203 9.33594V2.66927C1.33203 1.93594 1.93203 1.33594 2.66536 1.33594H9.33203C10.0654 1.33594 10.6654 1.93594 10.6654 2.66927"
  //         stroke="#565656"
  //         strokeWidth="1.33333"
  //         strokeLinecap="round"
  //         strokeLinejoin="round"
  //       />
  //     </svg>
  //   ),
  //   label: "Copy",
  //   danger: false,
  // },
  {
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 4H14"
          stroke="#D32F2F"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.6654 4V13.3333C12.6654 14 11.9987 14.6667 11.332 14.6667H4.66536C3.9987 14.6667 3.33203 14 3.33203 13.3333V4"
          stroke="#D32F2F"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.33203 4.0026V2.66927C5.33203 2.0026 5.9987 1.33594 6.66536 1.33594H9.33203C9.9987 1.33594 10.6654 2.0026 10.6654 2.66927V4.0026"
          stroke="#D32F2F"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.66797 7.33594V11.3359"
          stroke="#D32F2F"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.33203 7.33594V11.3359"
          stroke="#D32F2F"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    label: "Delete",
    danger: true,
  },
];

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onPreview, onDelete, onEdit }) => (
  <div className={styles["template-card"]}>
    <div className={styles["template-card__header"]}>
      <span className={styles["template-card__title"]}>{template?.name}</span>
      {/* <CategoryBadge category={template.category} /> */}
    </div>

    <div className={styles["template-card__section"]}>
      <span className={styles["template-card__section-label"]}>Subject</span>
      <div className={styles["template-card__subject-box"]}>
        {template.subject}
      </div>
    </div>

    <div className={styles["template-card__section"]}>
      <span className={styles["template-card__section-label"]}>Preview</span>
      <p className={styles["template-card__preview-text"]}>
        {template?.body}
      </p>
    </div>

    <div className={styles["template-card__meta"]}>
      <span>Created {formatDate(template?.created_at)}</span>
      <span>Updated {formatDate(template?.updated_at)}</span>
    </div>

    <div className={styles["template-card__actions"]}>
      <button className={styles["template-card__preview-btn"]}
       onClick={() => onPreview(template)}
      >
        <span>👁</span> Preview
      </button>
      {ACTION_BUTTONS.map(({ icon, label, danger }) => (
        <button
          key={label}
          title={label}
          className={[
            styles["template-card__icon-btn"],
            danger ? styles["template-card__icon-btn--danger"] : "",
          ].join(" ")}
          onClick={
            label === "Delete"
              ? () => onDelete(template.id)
              : label === "Edit"
              ? () => onEdit(template)
              : undefined
          }
        >
          {icon}
        </button>
      ))}
    </div>
  </div>
);

const TemplatesToolbar: React.FC<TemplatesToolbarProps> = ({
  search,
  onSearch,
  onNewTemplate
}) => (
  <div className={styles["templates-toolbar"]}>
    <div className={styles["templates-toolbar__search"]}>
      <span className={styles["templates-toolbar__search-icon"]}>🔍</span>
      <input
        className={styles["templates-toolbar__search-input"]}
        placeholder="Search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
    </div>
    <button className={styles["templates-toolbar__btn"]}>▼ Filter</button>
    {/* <button className={styles["templates-toolbar__btn"]}>
      ▾ All Categories
    </button> */}
    <button
      className={`${styles["templates-toolbar__btn"]} ${styles["templates-toolbar__btn--primary"]}`}
    onClick={onNewTemplate}
    >
      + New Template
    </button>
  </div>
);

const EmailTemplatesTab: React.FC = () => {
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  //  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState<Template | null>(null);

  const getTemplates = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_HOST_API}mail-center/templates`,
      {
        params: {
          ...(search.trim() ? { search: search.trim() } : {}),
          page,
        },
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      }
    );
    return response.data;
  }, [search, page]);

    const {
      data: templatesData,
      isLoading,
      refetch,
    } = useQuery({
      queryKey: ["templates", search, page],
      queryFn: getTemplates,
    });
    const templates: Template[] = templatesData?.data?.data || [];
    const pagination = templatesData?.data?.pagination;

    const handleSearch = (value: string) => {
      const trimmed = value.trimStart();
      setSearch(trimmed);
      setPage(1);
    };

  return (
    <div className={styles["templates-tab"]}>
      <TemplatesToolbar 
        search={search} 
        onSearch={handleSearch} 
        onNewTemplate={() => setModalOpen(true)}
       />
      <div className={styles["templates-tab__grid"]}>
        {templates?.map((t) => (
          <div key={t.id} className={styles["templates-tab__cell"]}>
            <TemplateCard
              template={t}
              onPreview={setPreviewTemplate}
              onDelete={(id) => {
                setSelectedTemplateId(id);
                setDeleteModalOpen(true);
              }}
              onEdit={(template) => {
                setSelectedTemplateForEdit(template);
                setEditModalOpen(true);
              }}
            />
          </div>
        ))}
        {!isLoading && templates?.length === 0 && (
          <div className={styles["templates-tab__empty"]}>
            No templates found.
          </div>
        )}
      </div>

       {isLoading && (
           <div className={styles["templates-tab__grid"]}>
            <TemplateCardSkeleton />
            <TemplateCardSkeleton />
            <TemplateCardSkeleton />
          </div>
        )}
        
      {pagination && pagination.last_page > 1 && (
        <div className={styles["templates-tab__pagination"]}>
          <button
            className={styles["templates-tab__page-btn"]}
            disabled={pagination.current_page <= 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            ◀ Prev
          </button>

          {Array.from({ length: pagination.last_page }).map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <button
                key={pageNum}
                className={
                  pageNum === pagination.current_page
                    ? `${styles["templates-tab__page-btn"]} ${styles["templates-tab__page-btn--active"]}`
                    : styles["templates-tab__page-btn"]
                }
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            className={styles["templates-tab__page-btn"]}
            disabled={pagination.current_page >= pagination.last_page}
            onClick={() => setPage((prev) => Math.min(pagination.last_page, prev + 1))}
          >
            Next ▶
          </button>
        </div>
      )}

      <CreateTemplateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={refetch}
      />

      <TemplatePreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      <DeleteTemplateModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        templateId={selectedTemplateId}
        onDeleteSuccess={refetch}
      />

      <EditTemplateModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        template={selectedTemplateForEdit}
        onEditSuccess={refetch}
      />
    </div>
  );
};

export default EmailTemplatesTab;
